// FLOW Platform — Bottom-sliding sheets.
//
// Uses a native <dialog> element opened via showModal(). The browser's
// "top layer" guarantees the sheet is above every z-index on the page —
// including WP admin elements, theme overlays, or anything else that
// would otherwise fight our z-index. No more stacking-context battles.
//
// Focus management:
//   - When a sheet opens, we move focus to its heading (tabindex=-1).
//   - When it closes, focus returns to the trigger button.
//
// Body scroll is locked while a sheet is open (matches mobile patterns).

import { focusElement } from './a11y.js';
import { buildStatsBody } from './stats-view.js';
import { howCtl, HOW_SETTINGS } from './howto.js';

let dialog = null;    // the <dialog> element (our "host")
let sheet = null;     // inner panel (animated)
let body = null;      // swappable inner content
let heading = null;
let lastTrigger = null;
let isClosing = false;

function ensureMounted(rootMount) {
  if (dialog) return;

  // Reuse if already in DOM (double-bundle guard).
  const existing = document.querySelector('.flow-fht-dialog-host');
  if (existing) {
    dialog = existing;
    sheet  = dialog.querySelector('.flow-fht-sheet');
    body   = sheet  && sheet.querySelector('.flow-fht-sheet-body');
    heading = sheet && sheet.querySelector('.flow-fht-sheet-heading');
    return;
  }

  dialog = document.createElement('dialog');
  dialog.className = 'flow-fht-dialog-host';

  // Click on the backdrop (the dialog element itself, not its children)
  // closes the sheet.
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) close();
  });

  // The browser fires 'cancel' on Escape for modal dialogs. We intercept
  // and run our own close so scroll-lock and focus-restore happen.
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });

  sheet = document.createElement('div');
  sheet.className = 'flow-fht-sheet';
  sheet.setAttribute('role', 'document');

  // Drag handle (decorative).
  const handle = document.createElement('div');
  handle.className = 'flow-fht-sheet-handle';
  handle.setAttribute('aria-hidden', 'true');
  sheet.appendChild(handle);

  // Header bar with title + close button.
  const headerRow = document.createElement('div');
  headerRow.className = 'flow-fht-sheet-header';

  heading = document.createElement('h2');
  heading.id = 'flow-fht-sheet-heading';
  heading.className = 'flow-fht-sheet-heading';
  heading.setAttribute('tabindex', '-1');
  headerRow.appendChild(heading);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'flow-fht-sheet-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '\u00d7';
  closeBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    close();
  });
  headerRow.appendChild(closeBtn);

  sheet.appendChild(headerRow);

  body = document.createElement('div');
  body.className = 'flow-fht-sheet-body';
  sheet.appendChild(body);

  dialog.appendChild(sheet);

  // Mount on document.body. showModal() places it in the top layer
  // regardless of where it lives in the DOM, but body is conventional.
  (rootMount.ownerDocument.body || document.body).appendChild(dialog);
}

/**
 * Open a sheet by id.
 */
export function openSheet(rootMount, id, trigger, version, opts) {
  ensureMounted(rootMount);
  if (isClosing) return; // don't open mid-close-animation

  lastTrigger = trigger || null;
  body.innerHTML = '';

  if (id === 'help') {
    heading.textContent = 'How to play';
    body.appendChild(buildHelpContent());
  } else if (id === 'settings') {
    heading.textContent = 'Settings';
    body.appendChild(buildSettingsContent({ version }));
  } else if (id === 'stats') {
    heading.textContent = 'Stats';
    body.appendChild(buildStatsBody(opts || {}));
  } else {
    return;
  }

  // showModal() places the dialog in the browser top layer — above every
  // z-index, transform stacking context, and WP overlay on the page.
  if (!dialog.open) {
    dialog.showModal();
  }

  // Trigger the slide-in animation on the next frame (so the CSS
  // transition has a starting state to work from).
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      dialog.classList.add('open');
    });
  });

  document.body.style.overflow = 'hidden';
  setTimeout(() => focusElement(heading), 50);
}

function close() {
  if (!dialog || isClosing) return;
  isClosing = true;

  dialog.classList.remove('open');
  document.body.style.overflow = '';

  if (lastTrigger) {
    const t = lastTrigger;
    lastTrigger = null;
    setTimeout(() => focusElement(t), 0);
  }

  // Wait for the slide-out CSS transition (240ms) then close the dialog
  // so it leaves the top layer cleanly.
  setTimeout(() => {
    if (dialog && dialog.open) {
      dialog.close();
    }
    isClosing = false;
  }, 260);
}

// ----------------------------------------------------------------------
// Help content — the rules of the game.
// ----------------------------------------------------------------------

export function buildHelpContent() {
  const wrap = document.createElement('div');
  wrap.className = 'flow-fht-help';

  const intro = document.createElement('p');
  intro.innerHTML = '<strong>thruWord.</strong> ' +
    'Travel from the top word to the bottom word by filling in the ' +
    'blank slots between them.';
  wrap.appendChild(intro);

  const rule = document.createElement('p');
  rule.innerHTML = 'Each word you write must form a ' +
    '<strong>compound word</strong> with the word above it AND the word below it.';
  wrap.appendChild(rule);

  // A small worked example.
  const example = document.createElement('div');
  example.className = 'flow-fht-help-example';
  example.innerHTML =
    '<div class="flow-fht-help-example-row">SUN</div>' +
    '<div class="flow-fht-help-example-arrow">\u2193</div>' +
    '<div class="flow-fht-help-example-row flow-fht-help-example-bridge">LIGHT</div>' +
    '<div class="flow-fht-help-example-arrow">\u2193</div>' +
    '<div class="flow-fht-help-example-row">HOUSE</div>';
  wrap.appendChild(example);

  const explain = document.createElement('p');
  explain.textContent = 'SUNLIGHT and LIGHTHOUSE are both real compound ' +
    'words. So LIGHT works as the bridge.';
  wrap.appendChild(explain);

  const stars = document.createElement('p');
  /* 2.14.29: one hint a puzzle (Jeff, 2026-10-06: "reduce the hints from 4 to 1") */
  stars.innerHTML = '<strong>One hint.</strong> Each puzzle gives you one ' +
    'Letter hint \u2014 the count sits beside it. Solving without it ' +
    'is the better result.';
  wrap.appendChild(stars);

  const hints = document.createElement('ul');
  hints.className = 'flow-fht-help-list';
  hints.innerHTML =
    '<li><strong>Letter.</strong> Reveals the next letter of the bridge you are on, and the puzzle goes on.</li>' +
    '<li><strong>Show answers.</strong> Once the hint is used, its box becomes Show answers. It asks once, then ends the puzzle and shows the words.</li>' +
    '<li>The boxes in each slot always show how many letters the bridge word has.</li>';
  wrap.appendChild(hints);

  const tip = document.createElement('p');
  tip.className = 'flow-fht-help-tip';
  tip.innerHTML = '<strong>Tip.</strong> Any valid compound word works, ' +
    'not just the one the puzzle was designed around. Alternate bridges ' +
    'count as solves.';
  wrap.appendChild(tip);

  // The One-puzzle-a-day switch moved to Settings in 2.14.14, as in every game.
  return wrap;
}

// ----------------------------------------------------------------------
// Settings content — minimal for v0.
// ----------------------------------------------------------------------

function buildSettingsContent({ version }) {
  const wrap = document.createElement('div');
  wrap.className = 'flow-fht-settings';

  /* THE PLATFORM'S SETTINGS, in thruWord's own sheet (2.14.14): the same rows,
     words and order as every game's (assets/shared/flow-settings-v1.js) —
     One puzzle a day, the game's own switches, Reset, the build line. */
  try {
    if (window.flowBeta && window.flowBeta.mountOneADay) window.flowBeta.mountOneADay(wrap);
    /* 2.14.35, group E: the every-fifth offer to share the cognitive uses, one switch for every game */
    if (window.flowFinish && window.flowFinish.mountUsesOffer) window.flowFinish.mountUsesOffer(wrap);
  } catch (e) { /* the sheet still opens without it */ }

  const resetRow = document.createElement('div');
  resetRow.className = 'flow-fht-settings-row';

  const resetLabel = document.createElement('div');
  resetLabel.className = 'flow-fht-settings-label';
  resetLabel.innerHTML = '<strong>Reset my data on this device</strong>' +
    '<span class="flow-fht-settings-sub">Clears your stats, streak, and played puzzles on this device and starts you fresh.</span>';
  resetRow.appendChild(resetLabel);

  const resetBtn = document.createElement('button');
  resetBtn.type = 'button';
  resetBtn.className = 'flow-fht-button';
  resetBtn.textContent = 'Reset\u2026';
  resetBtn.addEventListener('click', () => {
    /* two taps, as in every game — never a browser dialog (2.14.14) */
    if (resetBtn.dataset.armed !== '1') {
      resetBtn.dataset.armed = '1';
      resetBtn.textContent = 'Tap again to reset';
      return;
    }
    try {
      // Exact keys we own.
      ['flow_guest_token', 'flow_fht_beta_stats', 'flow_fht_beta_played', 'flow_fht_beta_tier', 'flow_fht_howto_v1']
        .forEach((k) => window.localStorage.removeItem(k));
      // Plus any in-progress puzzle state (keyed flow_fht_state:<id>), and since 2.14.29
      // the free-play ones too (flow_fht_test_state:<id>). Those were left behind, so a
      // puzzle begun in free play was still begun after a reset; Next now reads them to
      // tell which puzzles have been played, and a reset has to forget that as well.
      const toRemove = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key && (key.indexOf('flow_fht_state:') === 0 || key.indexOf('flow_fht_test_state:') === 0)) {
          toRemove.push(key);
        }
      }
      toRemove.forEach((k) => window.localStorage.removeItem(k));
    } catch (e) { /* ignore */ }
    resetBtn.textContent = 'Done';
    resetBtn.disabled = true;
    setTimeout(() => { window.location.reload(); }, 400);
  });
  resetRow.appendChild(resetBtn);
  wrap.appendChild(resetRow);

  // ---- Keyboard: Enter / Backspace position ----
  const kbdRow = document.createElement('div');
  kbdRow.className = 'flow-fht-settings-row';

  const kbdLabel = document.createElement('div');
  kbdLabel.className = 'flow-fht-settings-label';
  kbdLabel.innerHTML = '<strong>Enter key on the right</strong>' +
    '<span class="flow-fht-settings-sub">On: Enter is bottom-right, Backspace bottom-left. Off: the reverse.</span>';
  kbdRow.appendChild(kbdLabel);

  function readEnterRight() {
    try {
      const v = window.localStorage.getItem('flow_fht_enter_right');
      return v === null ? true : v === '1';
    } catch (e) { return true; }
  }

  const kbdToggle = document.createElement('button');
  kbdToggle.type = 'button';
  kbdToggle.className = 'flow-fht-toggle';
  kbdToggle.setAttribute('role', 'switch');
  kbdToggle.setAttribute('aria-label', 'Enter key on the right');
  function paintToggle() {
    const on = readEnterRight();
    kbdToggle.setAttribute('aria-checked', on ? 'true' : 'false');
    kbdToggle.classList.toggle('is-on', on);
    kbdToggle.textContent = on ? 'On' : 'Off';
  }
  kbdToggle.addEventListener('click', () => {
    const next = !readEnterRight();
    try { window.localStorage.setItem('flow_fht_enter_right', next ? '1' : '0'); } catch (e) { /* ignore */ }
    paintToggle();
    // Reposition any live keyboard immediately.
    try { window.dispatchEvent(new Event('flow-fht-kbd-layout')); } catch (e) { /* ignore */ }
  });
  paintToggle();
  kbdRow.appendChild(kbdToggle);
  wrap.appendChild(kbdRow);
  // ---- How to play (2.14.37): the practice round opens itself on a fresh puzzle while this is on ----
  const howRow = document.createElement('div');
  howRow.className = 'flow-fht-settings-row';
  const howLabel = document.createElement('div');
  howLabel.className = 'flow-fht-settings-label';
  howLabel.innerHTML = '<strong>' + HOW_SETTINGS.label + '</strong><span class="flow-fht-settings-sub">' + HOW_SETTINGS.sub + '</span>';
  howRow.appendChild(howLabel);
  const howToggle = document.createElement('button');
  howToggle.type = 'button';
  howToggle.className = 'flow-fht-toggle';
  howToggle.setAttribute('role', 'switch');
  howToggle.setAttribute('aria-label', HOW_SETTINGS.label);
  const howOnNow = () => { const c = howCtl(); return c ? c.on() : true; };
  function paintHow() {
    const on = howOnNow();
    howToggle.setAttribute('aria-checked', on ? 'true' : 'false');
    howToggle.classList.toggle('is-on', on);
    howToggle.textContent = on ? 'On' : 'Off';
  }
  howToggle.addEventListener('click', () => { const c = howCtl(); if (c) c.setOn(!c.on()); paintHow(); });
  paintHow();
  howRow.appendChild(howToggle);
  if (howCtl()) wrap.appendChild(howRow);
  // the platform order: the game's own switches, then Reset
  wrap.appendChild(resetRow);

  // The "Beta: browse the library" switch is gone (2.14.13): the ‹ and Next ›
  // walkers in row two are the way into the library now, and the platform's
  // beta switch shows or hides them, as in every other game.

  const versionLine = document.createElement('p');
  versionLine.className = 'flow-fht-settings-version';
  /* the build line, worded as every game's: "thruWord b-1a2b3c4d · a FLOW Word Game"
     (2.14.31; Jeff, 2026-10-08: "W and G should be capitalized") */
  const build = (window.flowConfig && window.flowConfig.build) ? String(window.flowConfig.build) : ('v' + (version || '?'));
  versionLine.textContent = 'thruWord ' + build + ' \u00b7 a FLOW Word Game';
  wrap.appendChild(versionLine);

  return wrap;
}
